<script lang="ts">
  interface Props {
    inviteToken: string;
    partnerToken: string;
    attributionToken: string;
    csrfToken: string;
    inviteAccountName?: string;
    inviteByName?: string;
    inviteEmail?: string;
  }

  let { inviteToken = "", partnerToken = "", attributionToken = "", csrfToken = "", inviteAccountName = "", inviteByName = "", inviteEmail = "" }: Props = $props();

  let name = $state("");
  let companyName = $state("");
  let email = $state("");
  let password = $state("");
  let passwordConfirmation = $state("");
  let phone = $state("");
  let termsAccepted = $state(false);
  let loading = $state(false);
  let errorMessage = $state("");
  const needsOwnedAccount = $derived(!inviteToken && !partnerToken);
  let wizardStep = $state(1);
  const selectedPlan = "basic";

  const stepTitle = "Sign up for DataTurk.ai";

  const stepDescription = $derived.by(() => {
    if (!needsOwnedAccount) {
      return "Create your DataTurk.ai account to focus on improvements that actually move your business forward";
    }
    if (wizardStep === 1) return "We'll help you find what to work on to improve your business";
    if (wizardStep === 2) return "About your company";
    return "Pick a Plan";
  });

  const step1Ready = $derived(
    Boolean(termsAccepted && name && email && password && passwordConfirmation)
  );
  const step2Ready = $derived(Boolean(companyName));

  $effect(() => {
    if (inviteEmail) email = inviteEmail;
  });

  async function submitSignup() {
    loading = true;
    errorMessage = "";

    try {
      const formData = new FormData();
      formData.append("user[name]", name);
      formData.append("user[email]", email);
      formData.append("user[password]", password);
      formData.append("user[password_confirmation]", passwordConfirmation);
      if (needsOwnedAccount) {
        formData.append("user[owned_accounts_attributes][0][name]", companyName);
      }
      if (phone) {
        formData.append("user[phone]", phone);
      }
      if (termsAccepted) {
        formData.append("user[terms_of_service]", "1");
      }
      formData.append("authenticity_token", csrfToken);

      // Add invisible captcha fields
      const spinnerField = document.getElementById("invisible_captcha_spinner");
      if (spinnerField?.value) {
        formData.append("spinner", spinnerField.value);
      }

      let url = "/signup";
      if (inviteToken) {
        url += `?invite=${encodeURIComponent(inviteToken)}`;
      } else if (partnerToken) {
        url += `?partner=${encodeURIComponent(partnerToken)}`;
      } else if (attributionToken) {
        url += `?attribution=${encodeURIComponent(attributionToken)}`;
      }

      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      });

      if (response.ok) {
        window.location.href = "/customer";
      } else if (response.status === 422) {
        errorMessage = "Please fix the errors below and try again.";
      } else {
        errorMessage = "An error occurred. Please try again.";
      }
    } catch (error) {
      console.error("Signup error:", error);
      errorMessage = "An error occurred. Please try again.";
    } finally {
      loading = false;
    }
  }

  async function handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    await submitSignup();
  }

  function goToStep2() {
    errorMessage = "";
    wizardStep = 2;
  }

  function goToStep3() {
    errorMessage = "";
    wizardStep = 3;
  }

  function backToStep1() {
    errorMessage = "";
    wizardStep = 1;
  }

  function backToStep2() {
    errorMessage = "";
    wizardStep = 2;
  }

  function goToStep(step: number) {
    if (step === 1) {
      backToStep1();
      return;
    }

    if (step === 2 && step1Ready) {
      goToStep2();
    }

    if (step === 3 && step1Ready && step2Ready) {
      goToStep3();
    }
  }
</script>

<div class="space-y-8">
  <div class="text-center sm:text-left">
    <h1 class="text-2xl font-bold text-foreground sm:text-3xl">{stepTitle}</h1>
    <p class="mt-3 text-lg text-muted-foreground">{stepDescription}</p>
  </div>

  {#if inviteToken && (inviteByName || inviteAccountName)}
    <div class="rounded-xl border border-primary/20 bg-primary/10 p-5 shadow-sm">
      <p class="text-base text-primary">
        {#if inviteAccountName}
          You've been invited to join the <strong>{inviteAccountName}</strong> team{inviteByName ? ` by ${inviteByName}` : ""}.
        {:else if inviteByName}
          You've been invited by <strong>{inviteByName}</strong>.
        {:else}
          You've been invited to join a team on DataTurk.ai.
        {/if}
      </p>
      <p class="mt-2 text-sm text-muted-foreground">
        Already have an account?
        <a href="/login" class="font-medium text-primary underline">Sign in instead</a>
      </p>
      <p class="mt-2 text-sm text-muted-foreground">
        You were invited by someone already using DataTurk.ai. To join their company account, you must use the signup link in your invitation email.
      </p>
    </div>
  {:else}
    <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
      <p class="text-xl font-semibold text-foreground">First time sign up?</p>
      <p class="mt-2 text-base text-muted-foreground">Fill out the form below to create a new DataTurk.ai account and get started.</p>

      <div class="mt-5 flex flex-col gap-1.5 border-t border-border pt-4 sm:flex-row sm:gap-5">
        <p class="text-sm text-muted-foreground">
          Already have a login?
          <a href="/login" class="text-primary underline hover:text-primary/80">Sign in</a>
        </p>
        <p class="text-sm text-muted-foreground">
          Got an invite? Check your email for the invite link.
        </p>
      </div>
    </div>
  {/if}

  {#if errorMessage}
    <div class="rounded-xl border border-destructive/20 bg-destructive/10 p-5 shadow-sm">
      <p class="text-base text-destructive">{errorMessage}</p>
    </div>
  {/if}

  {#if needsOwnedAccount}
    <div class="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)] xl:items-start">
      <nav class="rounded-xl border border-border bg-muted/40 p-4 shadow-sm sm:p-5" aria-label="Signup steps">
        <ol class="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 xl:grid-cols-1">
          <li>
            <button
              type="button"
              onclick={() => goToStep(1)}
              disabled={loading}
              class={`flex w-full items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${wizardStep === 1 ? "border-primary bg-primary/10" : "border-border bg-background hover:bg-muted"} disabled:opacity-50`}
            >
              <span class={`mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full border text-sm font-semibold ${wizardStep === 1 ? "border-primary bg-primary text-primary-foreground" : wizardStep > 1 ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                {wizardStep > 1 ? "1" : "1"}
              </span>
              <span>
                <span class={`block text-xs font-semibold uppercase tracking-wide ${wizardStep === 1 ? "text-primary" : "text-muted-foreground"}`}>Step 1</span>
                <span class="mt-1 block text-base font-medium text-foreground">About you</span>
              </span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onclick={() => goToStep(2)}
              disabled={loading || (!step1Ready && wizardStep === 1)}
              class={`flex w-full items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${wizardStep === 2 ? "border-primary bg-primary/10" : "border-border bg-background hover:bg-muted"} disabled:opacity-50`}
            >
              <span class={`mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full border text-sm font-semibold ${wizardStep === 2 ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>
                2
              </span>
              <span>
                <span class={`block text-xs font-semibold uppercase tracking-wide ${wizardStep === 2 ? "text-primary" : "text-muted-foreground"}`}>Step 2</span>
                <span class="mt-1 block text-base font-medium text-foreground">About your company</span>
              </span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onclick={() => goToStep(3)}
              disabled={loading || (!step2Ready && wizardStep < 3)}
              class={`flex w-full items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${wizardStep === 3 ? "border-primary bg-primary/10" : "border-border bg-background hover:bg-muted"} disabled:opacity-50`}
            >
              <span class={`mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full border text-sm font-semibold ${wizardStep === 3 ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>
                3
              </span>
              <span>
                <span class={`block text-xs font-semibold uppercase tracking-wide ${wizardStep === 3 ? "text-primary" : "text-muted-foreground"}`}>Step 3</span>
                <span class="mt-1 block text-base font-medium text-foreground">Pick a Plan</span>
              </span>
            </button>
          </li>
        </ol>
      </nav>

      {#if wizardStep === 1}
        <form onsubmit={handleSubmit} class="space-y-6 rounded-xl border border-border bg-muted/40 p-5 shadow-sm sm:p-8">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">About you</h2>
          </div>

          <div>
            <label for="name" class="block text-base font-medium text-foreground">Full name</label>
            <input
              id="name"
              type="text"
              placeholder="John Doe"
              bind:value={name}
              required
              disabled={loading}
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            />
          </div>

          <div>
            <label for="email" class="block text-base font-medium text-foreground">Email</label>
            <input
              id="email"
              type="email"
              placeholder="your@email.com"
              bind:value={email}
              required
              disabled={loading}
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            />
          </div>

          <div>
            <label for="password" class="block text-base font-medium text-foreground">Password</label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              bind:value={password}
              required
              disabled={loading}
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            />
          </div>

          <div>
            <label for="password-confirmation" class="block text-base font-medium text-foreground">
              Confirm password
            </label>
            <input
              id="password-confirmation"
              type="password"
              placeholder="••••••••"
              bind:value={passwordConfirmation}
              required
              disabled={loading}
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            />
          </div>

          <div>
            <label for="phone" class="block text-base font-medium text-foreground">Phone (optional)</label>
            <input
              id="phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              bind:value={phone}
              disabled={loading}
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            />
          </div>

          <!-- Invisible captcha honeypot field (should remain empty) -->
          <input type="hidden" name="honeypotx" value="" />

          <div class="flex items-center">
            <input
              id="terms"
              type="checkbox"
              bind:checked={termsAccepted}
              disabled={loading}
              required
              class="h-4 w-4 rounded border-border"
            />
            <label for="terms" class="ml-2 cursor-pointer text-base text-foreground">
              I agree to the Terms of Service
            </label>
          </div>

          <div class="pt-2">
            <button
              type="button"
              onclick={goToStep2}
              disabled={loading || !step1Ready}
              class="w-full rounded-md bg-primary px-4 py-3 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            >
              Next: About your company
            </button>
          </div>
        </form>
      {:else if wizardStep === 2}
        <div class="space-y-6 rounded-xl border border-border bg-muted/40 p-5 shadow-sm sm:p-8">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Company info</h2>
            <p class="mt-1 text-sm text-muted-foreground">Tell us the name of the company account you want to create.</p>
          </div>

          <div>
            <label for="company-name" class="block text-base font-medium text-foreground">Company name (Account name)</label>
            <input
              id="company-name"
              type="text"
              placeholder="Your company"
              bind:value={companyName}
              required
              disabled={loading}
              class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            />
          </div>

          <div class="flex flex-col gap-3 pt-2 sm:flex-row">
            <button
              type="button"
              onclick={backToStep1}
              disabled={loading}
              class="flex-1 rounded-md border border-border bg-background px-4 py-3 text-base font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            >
              Go back
            </button>
            <button
              type="button"
              onclick={goToStep3}
              disabled={loading || !companyName}
              class="flex-1 rounded-md bg-primary px-4 py-3 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            >
              Next: Pick a Plan
            </button>
          </div>
        </div>
      {:else}
        <div class="space-y-6 rounded-xl border border-border bg-muted/40 p-5 shadow-sm sm:p-8">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Pick a plan</h2>
            <p class="mt-1 text-sm text-muted-foreground">You can compare options now, but all new accounts start on Basic. You can upgrade anytime later.</p>
          </div>

          <div class="grid gap-3 md:grid-cols-3">
            <div class="rounded-lg border border-primary bg-primary/10 p-4">
              <p class="text-sm font-semibold uppercase tracking-wide text-primary">Basic</p>
              <p class="mt-1 text-2xl font-bold text-foreground">$0</p>
              <p class="mt-2 text-sm text-muted-foreground">Included for all new accounts.</p>
              <p class="mt-3 inline-flex rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground">Selected</p>
            </div>

            <div class="rounded-lg border border-border bg-background p-4 opacity-80">
              <p class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Pro</p>
              <p class="mt-1 text-2xl font-bold text-foreground">Custom</p>
              <p class="mt-2 text-sm text-muted-foreground">Advanced reporting and deeper insights.</p>
              <p class="mt-3 text-xs font-medium text-muted-foreground">Upgrade from settings later</p>
            </div>

            <div class="rounded-lg border border-border bg-background p-4 opacity-80">
              <p class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Enterprise</p>
              <p class="mt-1 text-2xl font-bold text-foreground">Custom</p>
              <p class="mt-2 text-sm text-muted-foreground">Multi-location controls and premium support.</p>
              <p class="mt-3 text-xs font-medium text-muted-foreground">Upgrade from settings later</p>
            </div>
          </div>

          <input type="hidden" name="selected-plan" value={selectedPlan} />

          <div class="flex flex-col gap-3 pt-2 sm:flex-row">
            <button
              type="button"
              onclick={backToStep2}
              disabled={loading}
              class="flex-1 rounded-md border border-border bg-background px-4 py-3 text-base font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            >
              Go back
            </button>
            <button
              type="button"
              onclick={submitSignup}
              disabled={loading}
              class="flex-1 rounded-md bg-primary px-4 py-3 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </div>
        </div>
      {/if}
    </div>
  {:else}
    <form onsubmit={handleSubmit} class="space-y-6 rounded-xl border border-border bg-muted/40 p-5 shadow-sm sm:p-8">
      <div>
        <h2 class="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{inviteAccountName ? `Join ${inviteAccountName}` : "Create your account"}</h2>
        <p class="mt-1 text-sm text-muted-foreground">Enter your details below to create your login and accept the invite.</p>
      </div>

      <div>
        <label for="name" class="block text-base font-medium text-foreground">Full name</label>
        <input
          id="name"
          type="text"
          placeholder="John Doe"
          bind:value={name}
          required
          disabled={loading}
          class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
        />
      </div>

      <div>
        <label for="email" class="block text-base font-medium text-foreground">Email</label>
        <input
          id="email"
          type="email"
          placeholder="your@email.com"
          bind:value={email}
          required
          disabled={loading}
          class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
        />
      </div>

      <div>
        <label for="password" class="block text-base font-medium text-foreground">Password</label>
        <input
          id="password"
          type="password"
          placeholder="••••••••"
          bind:value={password}
          required
          disabled={loading}
          class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
        />
      </div>

      <div>
        <label for="password-confirmation" class="block text-base font-medium text-foreground">
          Confirm password
        </label>
        <input
          id="password-confirmation"
          type="password"
          placeholder="••••••••"
          bind:value={passwordConfirmation}
          required
          disabled={loading}
          class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
        />
      </div>

      <div>
        <label for="phone" class="block text-base font-medium text-foreground">Phone (optional)</label>
        <input
          id="phone"
          type="tel"
          placeholder="+1 (555) 123-4567"
          bind:value={phone}
          disabled={loading}
          class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
        />
      </div>

      <!-- Invisible captcha honeypot field (should remain empty) -->
      <input type="hidden" name="honeypotx" value="" />

      <div class="flex items-center">
        <input
          id="terms"
          type="checkbox"
          bind:checked={termsAccepted}
          disabled={loading}
          required
          class="h-4 w-4 rounded border-border"
        />
        <label for="terms" class="ml-2 cursor-pointer text-base text-foreground">
          I agree to the Terms of Service
        </label>
      </div>

      <button
        type="submit"
        disabled={loading || !termsAccepted}
        class="w-full rounded-md bg-primary px-4 py-3 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
      >
        {loading ? "Joining team..." : "Accept invite"}
      </button>
    </form>
  {/if}

  <div class="space-y-1 text-center text-base text-muted-foreground">
    <p>
      Already have an account?
      <a href="/login" class="font-medium text-primary hover:text-primary/80">Sign in</a>
    </p>
    <p>
      Got an email invite? Follow the link in your email
    </p>
  </div>
</div>
